import axios from 'axios'
import type { Message, MessageContent } from '@/store/chat/chatStore'

// Re-export types for convenience
export type { Message, MessageContent } from '@/store/chat/chatStore'

// API Base URL - matches the pattern from client.ts
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ? '/api/v1' : 'http://localhost:8880/api/v1')

// Create axios instance with auth interceptor
const chatApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add token from localStorage (matches existing API client pattern)
chatApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  console.log('[Chat API] Token from localStorage:', token ? 'exists' : 'missing')
  console.log('[Chat API] Request URL:', `${config.baseURL || ''}${config.url || ''}`)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export interface ConversationListResponse {
  uuid: string
  user_uuid: string
  title: string
  created_at: string
  updated_at: string
  message_count: number
  last_message_preview: string | null
}

export interface Conversation {
  uuid: string
  user_uuid: string
  title: string
  created_at: string
  updated_at: string
}

export interface ConversationResponse {
  uuid: string
  user_uuid: string
  title: string
  created_at: string
  updated_at: string
  messages: Message[]
}

export interface ChatRequest {
  message: string
  conversation_uuid?: string | null
  model?: string | null
}

export interface ModelInfo {
  model_id: string
  display_name: string
  provider: string
  credit_cost: number
  is_default: boolean
}

export const chatService = {
  // Get active AI models for playground
  async getModels(): Promise<ModelInfo[]> {
    const response = await chatApi.get<ModelInfo[]>('/chat/models')
    return response.data
  },

  // Get all conversations
  async getConversations(limit = 50, offset = 0): Promise<ConversationListResponse[]> {
    const response = await chatApi.get<ConversationListResponse[]>(
      `/chat/conversations?limit=${limit}&offset=${offset}`,
    )
    return response.data
  },

  // Create new conversation
  async createConversation(title: string): Promise<Conversation> {
    const response = await chatApi.post<Conversation>('/chat/conversations', { title })
    return response.data
  },

  // Get conversation with messages
  async getConversation(uuid: string): Promise<ConversationResponse> {
    const response = await chatApi.get<ConversationResponse>(`/chat/conversations/${uuid}`)
    return response.data
  },

  // Update conversation title
  async updateConversation(uuid: string, title: string): Promise<Conversation> {
    const response = await chatApi.patch<Conversation>(`/chat/conversations/${uuid}`, { title })
    return response.data
  },

  // Delete conversation
  async deleteConversation(uuid: string): Promise<void> {
    await chatApi.delete(`/chat/conversations/${uuid}`)
  },

  // Save message with parts (after streaming completes)
  async saveMessage(
    conversationUuid: string,
    message: {
      role: string
      content: string
      parts?: Record<string, unknown>[]
      generation_id?: string
    },
  ): Promise<void> {
    await chatApi.post(`/chat/conversations/${conversationUuid}/messages`, message)
  },

  // Send chat message with streaming and tool call events
  async sendMessage(
    message: string,
    conversationUuid: string | null,
    onChunk: (content: MessageContent) => void,
    onComplete: (conversationUuid?: string, generationId?: string) => void,
    onError: (error: Error) => void,
    model?: string | null,
    enabledTools?: string[] | null,
  ): Promise<void> {
    try {
      const token = localStorage.getItem('auth_token')

      const body: Record<string, unknown> = {
        message,
        conversation_uuid: conversationUuid,
        model,
      }
      // Carry the pre-first-message tool selection ONLY when starting a new
      // conversation. Ignored by the backend for existing conversations.
      if (!conversationUuid && enabledTools != null) {
        body.enabled_tools = enabledTools
      }

      const response = await fetch(`${API_BASE_URL}/chat/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        // Try to parse the error body for a user-friendly message
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const errorBody = await response.json()
          if (response.status === 403 && errorBody?.detail?.error === 'insufficient_credits') {
            errorMessage = `Out of credits. You need ${errorBody.detail.credits_required} credit(s) but have ${errorBody.detail.credits_available} remaining. Please top up your account.`
          } else if (response.status === 503) {
            errorMessage = 'The AI model is currently unavailable. Please try again in a moment.'
          } else if (errorBody?.detail?.message) {
            errorMessage = errorBody.detail.message
          } else if (typeof errorBody?.detail === 'string') {
            errorMessage = errorBody.detail
          }
        } catch {
          // keep default message if body isn't JSON
          if (response.status === 503) {
            errorMessage = 'The AI model is currently unavailable. Please try again in a moment.'
          }
        }
        throw new Error(errorMessage)
      }

      // Capture the conversation UUID from response headers
      const returnedConversationUuid = response.headers.get('X-Conversation-UUID')
      console.log('[Chat API] Conversation UUID from response:', returnedConversationUuid)

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) {
        throw new Error('No reader available')
      }

      let buffer = ''
      let accumulatedText = ''
      let generationId: string | undefined
      let completionFired = false

      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          // Only fire onComplete if generation_complete wasn't already received
          if (!completionFired) {
            onComplete(returnedConversationUuid || undefined, generationId)
          }
          break
        }

        const chunk = decoder.decode(value, { stream: true })
        buffer += chunk

        // Process complete JSON lines
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue

          try {
            const event = JSON.parse(line)

            if (event.type === 'text') {
              // Text content chunk - accumulate for display
              accumulatedText += event.content || ''
              onChunk({
                type: 'text',
                content: accumulatedText,
              })
            } else if (event.type === 'tool_call_start') {
              // Tool call started
              onChunk({
                type: 'tool_call',
                toolCall: {
                  tool_call_id: event.tool_call_id,
                  tool_name: event.tool_name,
                  arguments: event.arguments,
                  isExecuting: true,
                },
              })
            } else if (event.type === 'tool_call_end') {
              // Tool call completed - update the existing tool call
              onChunk({
                type: 'tool_call',
                toolCall: {
                  tool_call_id: event.tool_call_id,
                  tool_name: event.tool_name,
                  result: event.result,
                  success: event.success,
                  isExecuting: false,
                },
              })
            } else if (event.type === 'generation_complete') {
              // Capture generation ID and immediately trigger completion.
              // Do NOT wait for done=true — the HTTP connection may stay open
              // briefly (nginx keep-alive, proxies) causing onComplete to never fire.
              generationId = event.generation_id
              console.log('[Chat API] Generation ID:', generationId)
              console.log('[Chat API] generation_complete received — triggering onComplete')
              completionFired = true
              onComplete(returnedConversationUuid || undefined, generationId)
              return
            } else if (event.type === 'max_iterations_reached') {
              // Handle max iterations warning
              console.log('[Chat API] Max iterations reached:', event)
              onChunk({
                type: 'max_iterations',
                maxIterations: {
                  message: event.message,
                  iterations: event.iterations,
                  suggestion: event.suggestion,
                },
              })
            } else if (event.type === 'context_length_exceeded') {
              // Handle context length exceeded error
              console.log('[Chat API] Context length exceeded:', event)
              onChunk({
                type: 'context_length',
                contextLength: {
                  message: event.message,
                  suggestion: event.suggestion,
                },
              })
            } else if (event.type === 'output_truncated') {
              // Handle output truncated warning
              console.log('[Chat API] Output truncated:', event)
              onChunk({
                type: 'output_truncated',
                outputTruncated: {
                  message: event.message,
                  suggestion: event.suggestion,
                },
              })
            } else if (event.type === 'error') {
              // Handle backend errors (rate limit, model unavailable, etc.)
              console.error('[Chat API] Backend error:', event)
              completionFired = true
              onError(new Error(event.message || 'An error occurred'))
              return
            }
          } catch (e) {
            console.error('Failed to parse event:', line, e)
          }
        }
      }
    } catch (error) {
      onError(error as Error)
    }
  },
}
